/**
 * デイリーミッション（2026-10-06）。
 * その日の読み方（モード）を読者が選び、モードごとにミッションの中身が変わる。選べるのは3つ: 精霊を集める・精霊を育てる・素材を集める。
 * ミッションはモードごとの候補から日ごとに引く（全員同じ。日付とモードだけで決まる）。
 * 選んだモードには、その日（次の区切りまで）だけのバフが付く＝手に入りやすさの係数（経験値・素材・出会い）。物を渡すのではない。
 * バフは上がる向きだけ。魔力には効かせない（ミッションの報酬にも魔力は入れない）。
 * 一度選んだら、その日（次の区切りまで）は変えられない（選び直せると毎日全部のバフを取れてしまうため）。
 * 選ぶまではミッションもバフも無い（行動は数えておくので、選んだ時点でそれまでの分が反映される）。前の日の読み方は札に印を付けるだけ。
 *
 * 表の正本は Firestore（gachaConfig/dailyMissions・管理UIの「ゲームの仕組み › デイリーミッション」で編集）。
 * 管理UI（local-ui）も写しで読むので、import は相対パスだけ。
 */

import { parseRewards, type PresentReward } from "./presents";

export const DAILY_MISSION_CONFIG_PATH = "gachaConfig/dailyMissions";

export type DailyMode = "collect" | "grow" | "gather";
export const DAILY_MODES: DailyMode[] = ["collect", "grow", "gather"];

/**
 * 1日のあいだ数えるもの。読了は初めて読んだ本だけ（読んだ秒数は問わない・2026-10-07）
 */
export type DailyCounter =
  | "read"               // 本を読み終える
  | "read_party_genre"   // 手持ちの精霊と同じ属性の本を読み終える
  | "read_unowned_genre" // まだ精霊のいない属性の本を読み終える（全属性そろっていれば、どの本でも数える）
  | "novel_gacha"        // 本を引く
  | "spirit_gacha"       // 精霊ガチャを回す
  | "spirit_exp"         // 読書で精霊が経験値を得る（手持ち全員の合計）
  | "feed"               // 精霊に素材をあげる（回数）
  | "materials"          // 読書で素材を手に入れる（個数）
  | "adventure"          // 精霊を旅に出す
  | "gacha_read"         // ガチャで引いた本を読み終える（精霊との出会いの確定もこれで数える）
  | "encounter";         // 読書で精霊に出会う（バフの guaranteedEncounterAt があれば、その冊数目で必ず届く）

/**
 * 管理UIで選ぶときの名前と、読者に見せる文（{n} に目標の数が入る）。
 * short はホームの「今日の読み方」の欄に出す短い名前（幅が狭いので）
 */
export const DAILY_COUNTERS: Record<DailyCounter, { name: string; text: string; short: string }> = {
  read:               { name: "本を読み終える",                     text: "本を{n}冊読み終える",                       short: "読了 {n}冊" },
  read_party_genre:   { name: "手持ちの精霊と同じ属性の本を読み終える", text: "手持ちの精霊と同じ属性の本を{n}冊読み終える", short: "同じ属性 {n}冊" },
  read_unowned_genre: { name: "まだ精霊のいない属性の本を読み終える",   text: "まだ精霊のいない属性の本を{n}冊読み終える",   short: "新しい属性 {n}冊" },
  novel_gacha:        { name: "本を引く",                           text: "本を{n}回引く",                             short: "本を引く {n}回" },
  spirit_gacha:       { name: "精霊ガチャを回す",                     text: "精霊ガチャを{n}回まわす",                     short: "精霊ガチャ {n}回" },
  spirit_exp:         { name: "読書で精霊が経験値を得る",               text: "読書で精霊が経験値を{n}得る",                 short: "経験値 {n}" },
  feed:               { name: "精霊に素材をあげる",                   text: "精霊に素材を{n}回あげる",                     short: "素材をあげる {n}回" },
  materials:          { name: "読書で素材を手に入れる",                 text: "読書で素材を{n}個手に入れる",                 short: "素材 {n}個" },
  adventure:          { name: "精霊を旅に出す",                       text: "精霊を{n}回旅に出す",                         short: "旅立ち {n}回" },
  gacha_read:         { name: "引いた本を読み終える",                   text: "引いた本を{n}冊読み終える",                   short: "引いた本 {n}冊" },
  encounter:          { name: "読書で精霊に出会う",                     text: "読書で精霊に{n}回出会う",                     short: "出会い {n}回" },
};
export const DAILY_COUNTER_KEYS = Object.keys(DAILY_COUNTERS) as DailyCounter[];

export interface DailyMission {
  /** モードの中で一意（受け取り済みの記録に使う） */
  id: string;
  counter: DailyCounter;
  target: number;
  rewards: PresentReward[];
  /** 日ごとに引くときの重み（省略は 1） */
  weight?: number;
}

/**
 * その日だけのバフ（手に入りやすさの係数）。1 で変わらない。
 * 経験値＝読書と素材をあげたとき／素材＝読書で落ちる素材（端数は確率で切り上げ）／出会い＝館の出会いの確率（合計は100%まで）。
 * guaranteedEncounterAt が 1 以上なら、その日まだ出会っていなければ、引いた本をその冊数読んだ時点で必ず出会う（初読・読んだ秒数は問わない）
 */
export interface DailyBuff {
  expMultiplier: number;
  materialMultiplier: number;
  encounterMultiplier: number;
  guaranteedEncounterAt: number;
}

export interface DailyModeConfig {
  /** 読者に見せる札（読者の言葉で） */
  label: string;
  /** 札の下の一言 */
  description: string;
  /** 1日に出すミッションの数（候補から引く） */
  missionCount: number;
  /** ミッションの候補。全員がこなせるものだけを入れる（手持ちや枠の有無で詰まらないもの） */
  candidates: DailyMission[];
  buff: DailyBuff;
}

export interface DailyMissionConfig {
  modes: Record<DailyMode, DailyModeConfig>;
}

/** バフなし（どの係数も1） */
export const NO_DAILY_BUFF: DailyBuff = { expMultiplier: 1, materialMultiplier: 1, encounterMultiplier: 1, guaranteedEncounterAt: 0 };

/** 仮置き。報酬は素材を名指ししない形（ランダムな素材・精霊の経験値）にしてある。中身は管理UIで変える */
export const DEFAULT_DAILY_MISSIONS: DailyMissionConfig = {
  modes: {
    collect: {
      label: "精霊と会いたい",
      description: "本を引いて読み、館で精霊と出会う日",
      missionCount: 3,
      candidates: [
        { id: "c1", counter: "novel_gacha", target: 2, rewards: [{ type: "material_random", quantity: 2 }] },
        { id: "c2", counter: "encounter", target: 1, rewards: [{ type: "material_random", quantity: 3 }] },
        { id: "c3", counter: "spirit_gacha", target: 1, rewards: [{ type: "material_random", quantity: 3 }] },
        { id: "c4", counter: "gacha_read", target: 2, rewards: [{ type: "material_random", quantity: 3 }] },
        { id: "c5", counter: "read_unowned_genre", target: 1, rewards: [{ type: "material_random", quantity: 3 }] },
      ],
      buff: { ...NO_DAILY_BUFF, encounterMultiplier: 2, guaranteedEncounterAt: 3 },
    },
    grow: {
      label: "精霊を育てたい",
      description: "手持ちの精霊と同じ属性の本を読み、素材もあげる日",
      missionCount: 3,
      candidates: [
        { id: "g1", counter: "read", target: 1, rewards: [{ type: "spirit_exp", amount: 10 }] },
        { id: "g2", counter: "read_party_genre", target: 1, rewards: [{ type: "spirit_exp", amount: 15 }] },
        { id: "g3", counter: "feed", target: 1, rewards: [{ type: "spirit_exp", amount: 15 }] },
        { id: "g4", counter: "read", target: 2, rewards: [{ type: "spirit_exp", amount: 15 }] },
        { id: "g5", counter: "spirit_exp", target: 60, rewards: [{ type: "spirit_exp", amount: 15 }] },
      ],
      buff: { ...NO_DAILY_BUFF, expMultiplier: 1.5 },
    },
    gather: {
      label: "素材を集めたい",
      description: "たくさん読んで素材を拾い、精霊を旅に出す日",
      missionCount: 3,
      candidates: [
        { id: "m1", counter: "read", target: 2, rewards: [{ type: "material_random", quantity: 2 }] },
        { id: "m2", counter: "materials", target: 3, rewards: [{ type: "material_random", quantity: 3 }] },
        { id: "m3", counter: "adventure", target: 1, rewards: [{ type: "material_random", quantity: 3 }] },
        { id: "m4", counter: "gacha_read", target: 2, rewards: [{ type: "material_random", quantity: 3 }] },
        { id: "m5", counter: "materials", target: 6, rewards: [{ type: "material_random", quantity: 3 }] },
      ],
      buff: { ...NO_DAILY_BUFF, materialMultiplier: 1.5 },
    },
  },
};

const str = (v: unknown) => (typeof v === "string" ? v : "");
const num = (v: unknown, fallback: number, min: number, max: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
};

function parseBuff(raw: unknown, fallback: DailyBuff): DailyBuff {
  if (!raw || typeof raw !== "object") return fallback;
  const r = raw as Record<string, unknown>;
  return {
    expMultiplier: num(r.expMultiplier, fallback.expMultiplier, 1, 10),
    materialMultiplier: num(r.materialMultiplier, fallback.materialMultiplier, 1, 10),
    encounterMultiplier: num(r.encounterMultiplier, fallback.encounterMultiplier, 1, 10),
    guaranteedEncounterAt: Math.floor(num(r.guaranteedEncounterAt, fallback.guaranteedEncounterAt, 0, 100)),
  };
}

function parseMode(raw: unknown, fallback: DailyModeConfig): DailyModeConfig {
  if (!raw || typeof raw !== "object") return fallback;
  const r = raw as Record<string, unknown>;
  const seen = new Set<string>();
  const candidates: DailyMission[] = [];
  // 2026-10-06 より前の保存は missions（全部を毎日出す）
  const rows = Array.isArray(r.candidates) ? r.candidates : Array.isArray(r.missions) ? r.missions : [];
  for (const m of rows) {
    if (!m || typeof m !== "object") continue;
    const row = m as Record<string, unknown>;
    const counter = row.counter as DailyCounter;
    const target = Math.floor(Number(row.target));
    if (!DAILY_COUNTER_KEYS.includes(counter) || !Number.isFinite(target) || target < 1) continue;
    let id = str(row.id) || `m${candidates.length + 1}`;
    while (seen.has(id)) id = `${id}_`;
    seen.add(id);
    const weight = num(row.weight, 1, 0, 1000);
    candidates.push({ id, counter, target, rewards: parseRewards(row.rewards), ...(weight !== 1 ? { weight } : {}) });
  }
  return {
    label: str(r.label) || fallback.label,
    description: str(r.description),
    missionCount: Math.floor(num(r.missionCount, Array.isArray(r.missions) ? candidates.length : fallback.missionCount, 1, 20)),
    candidates,
    buff: parseBuff(r.buff, fallback.buff),
  };
}

/** 保存された値を読める形にそろえる（数えられない行・目標が1未満の行は捨てる。係数は1〜10） */
export function parseDailyMissionConfig(raw: unknown): DailyMissionConfig {
  const modes = ((raw && typeof raw === "object" ? (raw as Record<string, unknown>).modes : null) ?? null) as
    | Record<string, unknown>
    | null;
  if (!modes || typeof modes !== "object") return DEFAULT_DAILY_MISSIONS;
  return {
    modes: {
      collect: parseMode(modes.collect, DEFAULT_DAILY_MISSIONS.modes.collect),
      grow: parseMode(modes.grow, DEFAULT_DAILY_MISSIONS.modes.grow),
      gather: parseMode(modes.gather, DEFAULT_DAILY_MISSIONS.modes.gather),
    },
  };
}

/** 文字列から決まる乱数（同じ日・同じモードなら誰が引いても同じ並びになる） */
function seededRandom(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * その日に出すミッションを候補から引く（重みつき・同じ候補は2回引かない）。日付（配布の日の番号）とモードだけで決まるので全員同じ。
 * 出す順は候補の並び順
 */
export function drawDailyMissions(mode: DailyModeConfig, modeKey: DailyMode, day: number): DailyMission[] {
  const random = seededRandom(`${day}:${modeKey}`);
  const pool = mode.candidates.filter((m) => (m.weight ?? 1) > 0);
  const picked = new Set<string>();
  while (picked.size < Math.min(mode.missionCount, pool.length)) {
    const rest = pool.filter((m) => !picked.has(m.id));
    const total = rest.reduce((a, m) => a + (m.weight ?? 1), 0);
    let r = random() * total;
    const hit = rest.find((m) => (r -= m.weight ?? 1) < 0) ?? rest[rest.length - 1];
    picked.add(hit.id);
  }
  return mode.candidates
    .filter((m) => picked.has(m.id))
    .map((m) => ({ id: m.id, counter: m.counter, target: m.target, rewards: m.rewards }));
}

/** バフを読者に見せる言葉（係数が1のものは出さない） */
/**
 * 効果の文の型。{倍率}・{冊目} に数が入る。管理UIの「文言」（quests.daily.effect.*）で書き換えられ、サーバーがその文を渡す（daily-missions-server.ts）。
 * 空の文は「使わない」＝その効果の文を出さない（効果そのものは効く）
 */
export interface DailyBuffTemplates {
  exp: string;
  material: string;
  encounter: string;
  guaranteed: string;
}
/** 既定の文（「文言」の台帳 quests.daily.effect.* の既定と同じ）。何が増えるか分かるように「読書で」「確率」まで書く（「精霊との出会い×2」では伝わらなかった・2026-10-07） */
export const DEFAULT_DAILY_BUFF_TEMPLATES: DailyBuffTemplates = {
  exp: "経験値×{倍率}",
  material: "読書の素材×{倍率}",
  encounter: "読書で精霊に出会う確率{倍率}倍",
  guaranteed: "引いた本の{冊目}冊目で必ず出会う",
};

export function dailyBuffLabels(buff: DailyBuff, templates: DailyBuffTemplates = DEFAULT_DAILY_BUFF_TEMPLATES): string[] {
  const n = (v: number) => String(Math.round(v * 100) / 100);
  const fill = (template: string, vars: Record<string, string>) => template.replace(/\{([^{}]+)\}/g, (all, name: string) => vars[name] ?? all);
  const labels: string[] = [];
  if (buff.expMultiplier > 1) labels.push(fill(templates.exp, { 倍率: n(buff.expMultiplier) }));
  if (buff.materialMultiplier > 1) labels.push(fill(templates.material, { 倍率: n(buff.materialMultiplier) }));
  if (buff.encounterMultiplier > 1) labels.push(fill(templates.encounter, { 倍率: n(buff.encounterMultiplier) }));
  if (buff.guaranteedEncounterAt > 0) labels.push(fill(templates.guaranteed, { 冊目: String(buff.guaranteedEncounterAt) }));
  return labels.filter((label) => label !== "");
}

/** 素材の数に係数を掛ける（端数は確率で切り上げ。1.5 なら 1個→1個か2個） */
export function multiplyQuantity(quantity: number, multiplier: number, random: () => number = Math.random): number {
  const x = quantity * multiplier;
  const base = Math.floor(x);
  return base + (random() < x - base ? 1 : 0);
}

/** 読者に見せるミッションの文 */
export function dailyMissionText(m: Pick<DailyMission, "counter" | "target">): string {
  return DAILY_COUNTERS[m.counter].text.replace("{n}", String(m.target));
}

/** ホームの欄に出す短いミッションの文（読了 2冊 など） */
export function dailyMissionShortText(m: Pick<DailyMission, "counter" | "target">): string {
  return DAILY_COUNTERS[m.counter].short.replace("{n}", String(m.target));
}

export function isDailyMode(v: unknown): v is DailyMode {
  return typeof v === "string" && (DAILY_MODES as string[]).includes(v);
}
